import {
    TicketMessageEntity,
} from "@dropDesk/domain/entities/ticket/message/ticket_message.entity";
import {ConstantsKeys} from "@dropDesk/domain/entities/constants/constants_keys";
import {differentInMinutesBetweenDates} from "@dropDesk/utils/helpers/date_helper";
import {MessageChatType} from "@dropDesk/domain/entities/ticket/message/external/message_entity";
import {orderMessagesByCreatedAt} from "@dropDesk/utils/helpers/common";
import {UserEntity} from "@dropDesk/domain/entities/user/user.entity";
import {ChatMessageEntity} from "@dropDesk/domain/entities/messages/message_enums";
import {InternalChatMessageEntity} from "@dropDesk/domain/entities/internal_chat/messages/internal_chat_message_entity";

const isMessageFromParent = (message: ChatMessageEntity, idParent: string): boolean => {
    if (message instanceof TicketMessageEntity) {
        return message.idTicket === idParent;
    }
    if (message instanceof InternalChatMessageEntity) {
        return message.idInternalChat === idParent;
    }
    return false;
};

export const useDeleteMessageButton = (message: ChatMessageEntity, canChange: boolean, userLogged: UserEntity, idParent: string) => {
    return !!message.createdAt &&
        !message.deleted &&
        differentInMinutesBetweenDates(message.createdAt ?? '') <= ConstantsKeys.visibleButtonDeleteMessageInMinutes &&
        canChange &&
        message.senderId === userLogged.id &&
        isMessageFromParent(message, idParent) &&
        !message.automatic;
};

export const useEditMessageButton = (message: ChatMessageEntity, canChange: boolean, userLogged: UserEntity, idParent: string) => {
    return !!message.createdAt && !message.deleted &&
        differentInMinutesBetweenDates(message.createdAt ?? '') <= ConstantsKeys.visibleButtonEditMessageInMinutes &&
        canChange &&
        (
            message.type === MessageChatType.textMessage ||
            message.type === MessageChatType.documentMessage && !!message.documentMessage?.caption ||
            message.type === MessageChatType.imageMessage && !!message.imageMessage?.caption ||
            message.type === MessageChatType.stickerMessage && !!message.stickerMessage?.caption ||
            message.type === MessageChatType.videoMessage && !!message.videoMessage?.caption
        ) &&
        message.senderId === userLogged.id &&
        !message.isViewOnceError &&
        !message.isLargeMediaError &&
        !message.isReceiveError &&
        isMessageFromParent(message, idParent) &&
        !message.automatic;
};

export const useReplyButton = (message: ChatMessageEntity, canChange: boolean, idParent: string, isMeAttendingTicket: boolean, ticketIsOpen: boolean, userLogged: UserEntity) => {
    return !!message.createdAt &&
        !message.deleted &&
        !message.hasSendError &&
        canChange &&
        !message.isViewOnceError &&
        !message.isLargeMediaError &&
        !message.isReceiveError &&
        isMessageFromParent(message, idParent) &&
        (isMeAttendingTicket || ticketIsOpen || userLogged.isUserClient) &&
        !message.automatic;
};

export const useReactionButton = (message: ChatMessageEntity, canChange: boolean, idParent: string, isMeAttendingTicket: boolean, userLogged: UserEntity) => {
    return !!message.createdAt &&
        !message.deleted &&
        !message.hasSendError &&
        canChange &&
        !message.isViewOnceError &&
        !message.isLargeMediaError &&
        !message.isReceiveError &&
        isMessageFromParent(message, idParent) &&
        (isMeAttendingTicket || userLogged.isUserClient) &&
        !message.automatic;
};

export const useCanChangeReaction = (message: ChatMessageEntity, canChange: boolean, idParent: string) => {
    return !!message.createdAt &&
        !message.deleted &&
        !message.hasSendError &&
        canChange &&
        !message.isViewOnceError &&
        !message.isLargeMediaError &&
        !message.isReceiveError &&
        isMessageFromParent(message, idParent) &&
        !message.automatic;
};

export const useGetStatus = (message: ChatMessageEntity, userClient: UserEntity | null, userLogged: UserEntity, idParent: string, attendant?: UserEntity) => {
    if ((userLogged.isUserClient && isMessageFromParent(message, idParent)) || (userLogged.isAttendant)) {
        return message.getStatus(userClient, userLogged.role, attendant);
    }
    return undefined;
};

export const useImagesMessage = (messages: ChatMessageEntity[]) => {
    const newImagesMessage = messages.filter((message) => message.isValidPreviewMessage);
    orderMessagesByCreatedAt(newImagesMessage);
    return newImagesMessage;
};

export const areReactionsEqual = (prevReactions?: ChatMessageEntity[], nextReactions?: ChatMessageEntity[]) => {
    if (prevReactions?.length !== nextReactions?.length) {
        return false;
    }

    if (prevReactions && nextReactions) {
        orderMessagesByCreatedAt(prevReactions);
        orderMessagesByCreatedAt(nextReactions);
        for (let i = 0; i < prevReactions.length; i++) {
            const prevReaction = prevReactions[i].reactionMessage;
            const nextReaction = nextReactions[i].reactionMessage;

            if (prevReaction?.text !== nextReaction?.text) {
                return false;
            }
        }
    }
    return true;
};
